import * as React from 'react'

import { FinanceSpendingModuleRoutesVisibilityDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { Navigation } from 'modules/core/lib/navigation/context'
import type { NavigationPaths } from 'modules/core/lib/navigation/context'
import { buildModuleRoute, buildNavigation } from 'modules/core/lib/react-router'

import expensesSubModuleRoutes from 'modules/expenses/routes/expenses/expensesSubModule'
import procurementSubModuleRoutes from 'modules/procurement/routes/procurementSubModule'
import softwareManagementSubModuleRoutes from 'modules/software_management/routes/softwareManagementSubModule'

import { contactsRoutes } from './contacts'
import { purchaseInvoicesRoutes } from './purchaseInvoices'

const SpendingRootPage = React.lazy(() => import('modules/finance/pages/SpendingRootPage'))
const ReroutingIndex = React.lazy(() => import('modules/finance/pages/ReroutingIndex'))

const buildSettingItems = (
  navigation: Navigation<NavigationPaths>,
  expenseCustomizeSettings?: boolean | undefined,
  expenseManageExpenses?: boolean | undefined
) => {
  const settingsItems = {
    items: [
      {
        label: i18n.t('company_settings.finance.expenses.title'),
        href: navigation.settings.expenses.fullPath(),
      },
      {
        label: i18n.t('company_settings.finance.procurement.title'),
        href: navigation.settings.procurement.fullPath(),
      },
      {
        label: i18n.t('company_settings.finance.software.title'),
        href: navigation.settings.software.fullPath(),
      },
    ],
  }

  return expenseCustomizeSettings && expenseManageExpenses ? settingsItems : undefined
}

export const financeSpendingRoutes = buildNavigation((navigation) => {
  return [
    buildModuleRoute({
      path: navigation.finance.spending.path,
      element: <SpendingRootPage />,
      handle: {
        module: 'finance-spending',
        moduleName: () => i18n.t('sidebar.finance_spending'),
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: FinanceSpendingModuleRoutesVisibilityDocument,
          }).map(({ data }) => {
            const { expenseCustomizeSettings, expenseManageExpenses } = data.expenses._policies
            const {
              sidebarSeeExpensesSubModule,
              sidebarSeeExpensesProcurementPurchaseRequestSubLevelItem,
              sidebarSeeExpensesProcurementPurchaseOrderSubLevelItem,
            } = data.expenses._policies
            const { sidebarSeeSoftwareSubModule } = data.softwareManagement._policies
            const { sidebarSeeFinanceVendorsSubLevelItem, financialDocumentsManagePurchase } =
              data.finance._policies

            return {
              title: i18n.t('sidebar.finance_spending'),
              settings: buildSettingItems(
                navigation,
                expenseCustomizeSettings.can,
                expenseManageExpenses.can
              ),
              subNavigation: [
                {
                  id: 'expenses',
                  title: i18n.t('finance.modules.spending.expenses'),
                  href: navigation.finance.spending.expenses.fullPath(),
                  isVisible: sidebarSeeExpensesSubModule.can,
                },
                {
                  id: 'invoices',
                  title: i18n.t('finance.modules.spending.purchase_invoices'),
                  href: navigation.finance.spending.invoices.fullPath(),
                  isVisible: financialDocumentsManagePurchase.can,
                },
                {
                  id: 'procurement',
                  title: i18n.t('finance.modules.spending.procurement'),
                  href: navigation.finance.spending.procurement.fullPath(),
                  isVisible:
                    sidebarSeeExpensesProcurementPurchaseRequestSubLevelItem.can ||
                    sidebarSeeExpensesProcurementPurchaseOrderSubLevelItem.can,
                },
                {
                  id: 'software-management',
                  title: i18n.t('finance.modules.spending.software'),
                  href: navigation.finance.spending.software.fullPath(),
                  isVisible: sidebarSeeSoftwareSubModule.can,
                },
                {
                  id: 'vendors',
                  title: i18n.t('finance.modules.spending.vendors'),
                  href: navigation.finance.spending.vendors.fullPath(),
                  isVisible: sidebarSeeFinanceVendorsSubLevelItem.can,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <ReroutingIndex />,
        },
        ...expensesSubModuleRoutes(navigation),
        ...procurementSubModuleRoutes(navigation),
        ...softwareManagementSubModuleRoutes(navigation),
        ...contactsRoutes(navigation, 'vendor'),
        ...purchaseInvoicesRoutes(navigation),
      ],
    }),
  ]
})
